import React, { useCallback, useState, createContext } from "react";
import { useRouter } from "next/navigation";
import { OffersFilterType } from "@/helpers/paramsOffers.helper";
import { convertObjectToParam } from "@/helpers/params.helper";

export const initialFilters: OffersFilterType = {
  productType: "all",
  brand: null,
  model: null,
  price: [null, null],
  states: [],
  course: [null, null],
  year: [null, null],
  capacity: [null, null],
  power: [null, null],
  bodyType: [],
  gearBox: [],
  fuel: [],
  invoice: [],
  sort: "date-desc",
  page: 0,
};

export const FilterContext = createContext<{
  filters: OffersFilterType
  setFilters: any
  onChange: (e: { name: string; value: any }) => void
  onChangeNoRedirect: (e: { name: string; value: any }) => void
  generateURL: (page?: number) => string
}>({
  filters: initialFilters,
  setFilters: () => {},
  onChange: () => {},
  onChangeNoRedirect: () => {},
  generateURL: () => "",
})

const FilterProvider = ({ children }: { children: React.ReactNode }) => {
  const router = useRouter()
  const [filters, setFilters] = useState(initialFilters)

  const onChange = useCallback(
    (obj: { name: string; value: any }) => {
      const { name, value } = obj
      const newFilters: any = {
        ...filters,
        [name]: value,
      }
      if(name === "productType"){
        newFilters.brand = null
        newFilters.model = null
      }
      if(name === "brand"){
        newFilters.model = null
      }

      setFilters(newFilters)

      let finalUrl = `/oferty`
      if(newFilters.productType !== "all" || newFilters.brand){
        finalUrl = finalUrl.concat(`/${newFilters.productType}`)
      }
      if(newFilters.brand){
        finalUrl = finalUrl.concat(`/${newFilters.brand}`)
      }
      if(newFilters.model){
        finalUrl = finalUrl.concat(`/${newFilters.model}`)
      }

      let params = ""
      if(newFilters.price[0] !== null || newFilters.price[1] !== null){
        params = params.concat(`&price=${convertObjectToParam(newFilters.price)}`)
      }
      if(newFilters.states.length > 0){
        params = params.concat(`&states=${newFilters.states.join(",")}`)
      }
      if(newFilters.year[0] !== null || newFilters.year[1] !== null){
        params = params.concat(`&year=${convertObjectToParam(newFilters.year)}`)
      }
      if(newFilters.bodyType.length > 0){
        params = params.concat(`&bodyType=${newFilters.bodyType.join(",")}`)
      }
      if(newFilters.gearBox.length > 0){
        params = params.concat(`&gearBox=${newFilters.gearBox.join(",")}`)
      }
      if(newFilters.fuel.length > 0){
        params = params.concat(`&fuel=${newFilters.fuel.join(",")}`)
      }
      if(newFilters.invoice.length > 0){
        params = params.concat(`&invoice=${newFilters.invoice.join(",")}`)
      }
      if(newFilters.course[0] !== null || newFilters.course[1] !== null){
        params = params.concat(`&course=${convertObjectToParam(newFilters.course)}`)
      }
      if(newFilters.capacity[0] !== null || newFilters.capacity[1] !== null){
        params = params.concat(`&capacity=${convertObjectToParam(newFilters.capacity)}`)
      }
      if(newFilters.power[0] !== null || newFilters.power[1] !== null){
        params = params.concat(`&power=${convertObjectToParam(newFilters.power)}`)
      }
      if(newFilters.sort !== "date-desc"){
        params = params.concat(`&sort=${newFilters.sort}`)
      }
      if(newFilters.p && newFilters.p !== 0){
        params = params.concat(`&p=${newFilters.p}`)
      }

      router.push(`${finalUrl}${params !== "" ? `?${params}` : ""}`)
    },
    [filters],
  )

  const onChangeNoRedirect = useCallback(
    (obj: { name: string; value: any }) => {
      const { name, value } = obj
      const newFilters: any = {
        ...filters,
        [name]: value,
      }
      if(name === "productType"){
        newFilters.brand = null
        newFilters.model = null
      }
      if(name === "brand"){
        newFilters.model = null
      }

      setFilters(newFilters)
    },
    [filters],
  )

  const generateURL = useCallback(
    (newPage?: number) => {
      let finalUrl = `/oferty`
      if(filters.productType !== "all" || filters.brand){
        finalUrl = finalUrl.concat(`/${filters.productType}`)
      }
      if(filters.brand){
        finalUrl = finalUrl.concat(`/${filters.brand}`)
      }
      if(filters.model){
        finalUrl = finalUrl.concat(`/${filters.model}`)
      }

      let params = ""
      if(filters.price[0] !== null || filters.price[1] !== null){
        params = params.concat(`&price=${convertObjectToParam(filters.price)}`)
      }
      if(filters.states.length > 0){
        params = params.concat(`&states=${filters.states.join(",")}`)
      }
      if(filters.year[0] !== null || filters.year[1] !== null){
        params = params.concat(`&year=${convertObjectToParam(filters.year)}`)
      }
      if(filters.bodyType.length > 0){
        params = params.concat(`&bodyType=${filters.bodyType.join(",")}`)
      }
      if(filters.gearBox.length > 0){
        params = params.concat(`&gearBox=${filters.gearBox.join(",")}`)
      }
      if(filters.fuel.length > 0){
        params = params.concat(`&fuel=${filters.fuel.join(",")}`)
      }
      if(filters.invoice.length > 0){
        params = params.concat(`&invoice=${filters.invoice.join(",")}`)
      }
      if(filters.course[0] !== null || filters.course[1] !== null){
        params = params.concat(`&course=${convertObjectToParam(filters.course)}`)
      }
      if(filters.capacity[0] !== null || filters.capacity[1] !== null){
        params = params.concat(`&capacity=${convertObjectToParam(filters.capacity)}`)
      }
      if(filters.power[0] !== null || filters.power[1] !== null){
        params = params.concat(`&power=${convertObjectToParam(filters.power)}`)
      }
      if(newPage && newPage !== 0){
        params = params.concat(`&p=${newPage}`)
      }

      return `${finalUrl}${params !== "" ? `?${params}` : ""}`
    },
    [filters],
  )

  return (
    <FilterContext.Provider value={{ filters, setFilters, onChange, onChangeNoRedirect, generateURL }}>
      {children}
    </FilterContext.Provider>
  );
};

export default FilterProvider;
